import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@cloudgenie/ui';
import { API_BASE, useAuth, hasCap } from '../AuthContext';

/** Wave 5 Phase 2c — pricing + subscription self-service.
 *
 * Uses the /api/billing/v2 endpoints shipped in Phase 2b:
 *   - GET  /products        → catalog for pricing tiles
 *   - GET  /subscription    → current tenant plan (defaults to `free`)
 *   - POST /checkout-session → returns Stripe Checkout URL to redirect to
 *   - POST /portal-session   → returns Stripe Customer Portal URL
 *
 * Redirect targets (approved by user):
 *   Success → id.cloudgenie.co/billing?status=success
 *   Cancel  → id.cloudgenie.co/billing?status=cancelled
 *
 * Backend actually builds:
 *   success_url = {origin}/billing/success?session_id={CHECKOUT_SESSION_ID}
 *   cancel_url  = {origin}/billing?canceled=1
 * We handle both shapes below to stay compatible.
 */

interface Price {
  product_slug: string;
  interval: 'month' | 'year';
  currency: string;
  unit_amount: number;
  stripe_price_id: string | null;
  active: boolean;
}
interface Product {
  slug: string;
  name: string;
  description: string;
  features: Record<string, boolean | number>;
  prices: Price[];
  active: boolean;
}
interface Subscription {
  tenant_id: string;
  plan_slug: string;
  status: string;
  cancel_at_period_end?: boolean;
  current_period_end?: string | null;
  features?: Record<string, boolean | number>;
  stripe_subscription_id?: string | null;
}

type Interval = 'month' | 'year';

/** Marketing copy — small map of extras rendered on each tile alongside API features. */
const FEATURE_ROWS: Array<{ key: string; label: string }> = [
  { key: 'finops.dashboards', label: 'FinOps dashboards' },
  { key: 'finops.pro',        label: 'Multi-cloud budgets & anomalies' },
  { key: 'studio.editor',     label: 'Studio IaC editor' },
  { key: 'studio.pro',        label: 'Policy scanning + pipelines' },
  { key: 'seats.max',         label: 'Seats included' },
  { key: 'api.rate.perMinute', label: 'API calls / min' },
  { key: 'sso.saml',          label: 'SSO / SAML' },
  { key: 'audit.export',      label: 'Audit log export' },
];

function formatMoney(minor: number, currency: string): string {
  const symbol = currency.toLowerCase() === 'gbp' ? '£'
               : currency.toLowerCase() === 'usd' ? '$'
               : currency.toLowerCase() === 'eur' ? '€'
               : currency.toUpperCase() + ' ';
  return `${symbol}${(minor / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatFeatureValue(v: boolean | number | undefined): string {
  if (v === true) return '✓';
  if (v === false || v === undefined) return '—';
  if (typeof v === 'number') return v < 0 ? 'Unlimited' : String(v);
  return String(v);
}

export default function BillingPage() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Backend success URL: /billing/success?session_id=cs_test_...
  // Backend cancel URL:  /billing?canceled=1
  // We also honour ?status=success|cancelled as the user-facing convention.
  const pathIsSuccess = typeof window !== 'undefined' && window.location.pathname.endsWith('/billing/success');
  const status = params.get('status')
    || (pathIsSuccess || params.get('session_id') ? 'success'
    : params.get('canceled') === '1' || params.get('cancelled') === '1' ? 'cancelled' : null);

  const [interval, setInterval] = useState<Interval>('month');
  const [products, setProducts] = useState<Product[] | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  // Guard: not authenticated → bounce to login preserving return path.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Load catalog + subscription in parallel.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [prodRes, subRes] = await Promise.all([
          fetch(`${API_BASE}/api/billing/v2/products`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/billing/v2/subscription`, { credentials: 'include' }),
        ]);
        if (!prodRes.ok) throw new Error(`Failed to load plans (${prodRes.status})`);
        if (!subRes.ok) throw new Error(`Failed to load subscription (${subRes.status})`);
        const prodJson = (await prodRes.json()) as Product[];
        const subJson = (await subRes.json()) as Subscription;
        if (cancelled) return;
        setProducts(prodJson);
        setSubscription(subJson);
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'Failed to load billing.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const paidProducts = useMemo(
    () => (products || []).filter(p => p.slug !== 'free' && p.slug !== 'enterprise' && p.prices.length > 0),
    [products],
  );
  const freeProduct = useMemo(() => (products || []).find(p => p.slug === 'free'), [products]);
  const enterpriseProduct = useMemo(() => (products || []).find(p => p.slug === 'enterprise'), [products]);

  const currentSlug = subscription?.plan_slug || 'free';
  const canManageBilling = hasCap(user, 'billing.manage');
  const canReadBilling = hasCap(user, 'billing.read') || canManageBilling;

  async function startCheckout(plan_slug: string) {
    setBusySlug(plan_slug);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/billing/v2/checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_slug,
          interval,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = res.status === 503
          ? 'Billing is not enabled in this environment yet. Try again in production.'
          : (typeof data?.detail === 'string' ? data.detail : `Checkout failed (${res.status}).`);
        setError(msg);
        return;
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      setError((e as Error).message || 'Checkout failed.');
    } finally {
      setBusySlug(null);
    }
  }

  async function openPortal() {
    setPortalBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/billing/v2/portal-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: `${window.location.origin}/billing` }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = res.status === 503
          ? 'Billing portal is not enabled in this environment yet.'
          : (typeof data?.detail === 'string' ? data.detail : `Could not open portal (${res.status}).`);
        setError(msg);
        return;
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      setError((e as Error).message || 'Could not open portal.');
    } finally {
      setPortalBusy(false);
    }
  }

  if (authLoading || (!user && !authLoading)) {
    return <BillingChrome><p className="text-center text-sm text-muted-foreground py-16" data-testid="billing-loading">Loading…</p></BillingChrome>;
  }

  return (
    <BillingChrome>
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-6" data-testid="billing-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-2">Billing</p>
            <h1 className="text-4xl sm:text-5xl font-semibold">Plans &amp; subscription</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              You&apos;re on the <strong data-testid="current-plan-slug">{currentSlug}</strong> plan
              {subscription?.status && subscription.status !== 'active' ? (
                <> · <span className="text-yellow-400">{subscription.status}</span></>
              ) : null}.
              {subscription?.stripe_subscription_id
                ? ' Manage payment methods or cancel anytime.'
                : ' Upgrade to unlock every FinOps and Studio feature.'}
            </p>
          </div>

          {subscription?.stripe_subscription_id && canManageBilling ? (
            <Button
              variant="outline"
              onClick={openPortal}
              disabled={portalBusy}
              data-testid="manage-subscription"
            >
              {portalBusy ? 'Opening…' : 'Manage subscription'}
            </Button>
          ) : null}
        </div>

        {!canReadBilling && (
          <div role="alert" data-testid="billing-no-permission" className="mt-6 rounded-lg border border-yellow-700/50 bg-yellow-900/20 text-yellow-200 px-4 py-3 text-sm">
            Your role ({user?.role || 'unknown'}) can view plans but can&apos;t manage billing.
            Ask an owner to upgrade or grant you the <strong>billing</strong> role.
          </div>
        )}

        {status === 'success' && (
          <div
            role="status"
            data-testid="billing-status-success"
            className="mt-6 rounded-lg border border-emerald-700/50 bg-emerald-900/20 text-emerald-200 px-4 py-3 text-sm"
          >
            Payment received — thanks! Your plan will activate as soon as Stripe confirms.
          </div>
        )}
        {status === 'cancelled' && (
          <div
            role="status"
            data-testid="billing-status-cancelled"
            className="mt-6 rounded-lg border border-yellow-700/50 bg-yellow-900/20 text-yellow-200 px-4 py-3 text-sm"
          >
            Checkout cancelled — no charges were made.
          </div>
        )}
        {error && (
          <div role="alert" data-testid="billing-error" className="mt-6 rounded-lg border border-red-700/50 bg-red-900/20 text-red-200 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Interval toggle */}
        <div className="mt-10 flex items-center justify-center">
          <div className="inline-flex rounded-full border border-border p-1 bg-card" data-testid="interval-toggle">
            <button
              type="button"
              onClick={() => setInterval('month')}
              data-testid="interval-month"
              className={`px-4 py-1.5 rounded-full text-sm transition ${interval === 'month' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval('year')}
              data-testid="interval-year"
              className={`px-4 py-1.5 rounded-full text-sm transition ${interval === 'year' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Yearly <span className="text-emerald-400 ml-1">−17%</span>
            </button>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="billing-skeleton">
            {[0, 1, 2].map(i => (
              <div key={i} className="rounded-xl border border-border bg-card p-6 animate-pulse h-96" />
            ))}
          </div>
        )}

        {/* Pricing tiles */}
        {!loading && products && (
          <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="pricing-grid">
            {paidProducts.map(p => (
              <PricingTile
                key={p.slug}
                product={p}
                interval={interval}
                current={currentSlug === p.slug}
                highlight={p.slug === 'bundle_pro'}
                busy={busySlug === p.slug}
                canUpgrade={canManageBilling}
                onSelect={() => startCheckout(p.slug)}
              />
            ))}
          </div>
        )}

        {/* Free + Enterprise strip */}
        {!loading && products && (
          <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="pricing-extras">
            {freeProduct && (
              <FreeCard product={freeProduct} current={currentSlug === 'free'} />
            )}
            {enterpriseProduct && (
              <EnterpriseCard product={enterpriseProduct} />
            )}
          </div>
        )}

        <p className="mt-10 text-center text-xs text-muted-foreground">
          Prices in GBP. All plans use Stripe Test Mode during our beta —{' '}
          <Link to="/" className="hover:text-foreground underline">back to dashboard</Link>.
        </p>
      </section>
    </BillingChrome>
  );
}

/* ----------------------------- sub components ----------------------------- */

function BillingChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="shell-id-billing">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2 group" data-testid="billing-brand">
            <span className="inline-block w-6 h-6 rounded-md bg-primary" />
            <span className="font-semibold tracking-tight">CloudGenie</span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="billing-back-home">
            ← Back to account
          </Link>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-border/40">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} CloudGenie</p>
          <div className="flex items-center gap-4">
            <a className="hover:text-foreground" href="https://www.cloudgenie.co">Home</a>
            <a className="hover:text-foreground" href="https://www.cloudgenie.co/legal/privacy">Privacy</a>
            <a className="hover:text-foreground" href="https://www.cloudgenie.co/legal/terms">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

interface PricingTileProps {
  product: Product;
  interval: Interval;
  current: boolean;
  highlight: boolean;
  busy: boolean;
  canUpgrade: boolean;
  onSelect: () => void;
}

function PricingTile({ product, interval, current, highlight, busy, canUpgrade, onSelect }: PricingTileProps) {
  const price = product.prices.find(p => p.interval === interval);
  const priceStr = price ? formatMoney(price.unit_amount, price.currency) : '—';
  const suffix = interval === 'month' ? '/ month' : '/ year';

  return (
    <div
      data-testid={`plan-tile-${product.slug}`}
      className={`relative rounded-xl border p-6 flex flex-col ${
        highlight
          ? 'border-primary/60 bg-card shadow-[0_0_24px_0_hsl(217_91%_60%_/_0.15)]'
          : 'border-border bg-card'
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-6 rounded-full bg-primary text-primary-foreground text-[10px] uppercase tracking-widest px-2 py-1" data-testid="plan-badge-popular">
          Most popular
        </span>
      )}
      <div className="mb-4">
        <div className="text-lg font-semibold">{product.name}</div>
        <p className="text-sm text-muted-foreground mt-1">{product.description}</p>
      </div>

      <div className="mb-6">
        <span className="text-4xl font-semibold" data-testid={`plan-price-${product.slug}`}>{priceStr}</span>
        <span className="text-sm text-muted-foreground ml-1">{suffix}</span>
      </div>

      <ul className="space-y-2 text-sm mb-6">
        {FEATURE_ROWS.map(row => {
          const v = product.features[row.key];
          const shown = v !== undefined && v !== false;
          if (!shown) return null;
          return (
            <li key={row.key} className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span className="text-muted-foreground">
                {row.label}
                {typeof v === 'number' ? <span className="text-foreground"> — {formatFeatureValue(v)}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-auto">
        {current ? (
          <Button variant="outline" disabled data-testid={`plan-current-${product.slug}`}>Current plan</Button>
        ) : !canUpgrade ? (
          <Button variant="outline" disabled data-testid={`plan-locked-${product.slug}`}>
            Billing role required
          </Button>
        ) : (
          <Button
            variant={highlight ? 'primary' : 'outline'}
            onClick={onSelect}
            disabled={busy || !price}
            data-testid={`plan-upgrade-${product.slug}`}
          >
            {busy ? 'Redirecting…' : price ? `Upgrade to ${product.name}` : 'Unavailable'}
          </Button>
        )}
      </div>
    </div>
  );
}

function FreeCard({ product, current }: { product: Product; current: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="plan-tile-free">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold">{product.name}</div>
          <p className="text-sm text-muted-foreground mt-1">{product.description}</p>
        </div>
        <div>
          <span className="text-3xl font-semibold">£0</span>
          <span className="text-sm text-muted-foreground ml-1">forever</span>
        </div>
      </div>
      <div className="mt-4">
        {current ? (
          <span className="inline-block rounded-full border border-primary/50 bg-primary/10 text-primary px-3 py-1 text-xs" data-testid="plan-current-free">Current plan</span>
        ) : (
          <span className="text-xs text-muted-foreground">Downgrade from the customer portal.</span>
        )}
      </div>
    </div>
  );
}

function EnterpriseCard({ product }: { product: Product }) {
  return (
    <div className="rounded-xl border border-studio/50 bg-card p-6" data-testid="plan-tile-enterprise">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold">{product.name}</div>
          <p className="text-sm text-muted-foreground mt-1">{product.description}</p>
        </div>
        <div className="text-sm text-muted-foreground">Custom pricing</div>
      </div>
      <div className="mt-4">
        <a href="mailto:sales@cloudgenie.co" data-testid="plan-contact-sales">
          <Button variant="outline">Contact sales</Button>
        </a>
      </div>
    </div>
  );
}
